import type {
	Floor,
	GeoJSONPoint,
	Location,
	Sensor,
	Zone,
	ZoneBoundary,
} from "@sitescene/types";

import { normalizeGeoJsonPolygon } from "@sitescene/shared";
import { DisplayMode, LocationStatus } from "@sitescene/types";

import type {
	ShadowFloor,
	ShadowLocation,
	ShadowSensor,
	ShadowZone,
} from "../../types/location-map";

import { getRefId } from "./installation-ids";

const toGeoJsonPoint = (
	point: GeoJSONPoint | null | undefined,
): GeoJSONPoint | undefined => {
	if (!point || point.type !== "Point") return undefined;
	const [lng, lat] = point.coordinates;
	if (typeof lng !== "number" || typeof lat !== "number") return undefined;
	return point;
};

const toZoneBoundary = (
	boundary: null | undefined | Zone["boundary"],
): null | undefined | ZoneBoundary => {
	if (boundary === null) return null;
	if (!boundary) return undefined;
	if (boundary.type === "Polygon" || boundary.type === "LineString") {
		return boundary;
	}
	return undefined;
};

const adaptSensor = (sensor: Sensor): ShadowSensor => ({
	...sensor,
	geolocation:
		sensor.geolocation === null ? null : toGeoJsonPoint(sensor.geolocation),
});

const adaptZone = (zone: Zone): ShadowZone => {
	const sensors = (zone.sensors ?? []).map(adaptSensor);

	return {
		...zone,
		boundary: toZoneBoundary(zone.boundary),
		sensorIds: sensors.map((sensor) => sensor.id),
		sensors,
	};
};

const adaptFloor = (floor: Floor, zones: ShadowZone[]): ShadowFloor => {
	const boundary = floor.boundary
		? (normalizeGeoJsonPolygon(floor.boundary) ?? undefined)
		: floor.boundary;
	return {
		...floor,
		...(boundary ? { boundary } : {}),
		zones: zones.filter((zone) => getRefId(zone.floor) === floor.id),
	};
};

/**
 * Map a hydrated API location into the location-management {@link ShadowLocation} view.
 *
 * Passes domain fields through; only normalizes GeoJSON, applies location
 * defaults, and nests adapted floors, zones, and sensors (with denormalized
 * `sensorIds`).
 */
export const adaptApiLocation = (location: Location): ShadowLocation => {
	const {
		floors: _unusedFloors,
		sensors: _unusedSensors,
		zones: _unusedZones,
		...rest
	} = location;

	const zones = (location.zones ?? []).map(adaptZone);

	return {
		...rest,
		coordinates: toGeoJsonPoint(location.coordinates),
		displayMode: location.displayMode ?? DisplayMode.ROADMAP,
		floors: (location.floors ?? []).map((floor) => adaptFloor(floor, zones)),
		status: location.status ?? LocationStatus.ACTIVE,
		zones,
	};
};

export const adaptApiLocations = (locations: Location[]): ShadowLocation[] =>
	locations.map(adaptApiLocation);
